/**
 * @file dashboard-page-engine.ts
 *
 * Abstract base class that provides a complete, API-backed dashboard layout
 * engine for any page component.
 *
 * ## What it gives you for free
 *  - Signals: dashboardId, layoutItems, layoutLoading, layoutError,
 *             layoutSaving, isMixedDashboard, availableDashboards
 *  - Computeds: orderedDashboardCards, hiddenDashboardCards
 *  - Actions: load, drag-drop reorder, visibility toggle, widget resize,
 *             per-widget custom config, save-as-new layout, set-default,
 *             delete layout, reset layout, mixed-mode toggle,
 *             add-widgets-from-catalog dialog
 *  - Template helpers: getCardTitle, cardWidthPercent, cardAccentStyle,
 *                      trackDashboardCard
 *
 * ## How to adopt for a new page
 *
 * ```ts
 * @Component({ ... })
 * export class MySuperPageComponent extends DashboardPageEngine<MySectionId> {
 *   protected readonly dashboardEngineConfig: DashboardEngineConfig<MySectionId> = {
 *     pageId: MY_PAGE_ID,              // backend pages.id
 *     cardConfig: MY_SECTION_CARD_CONFIG,
 *     isCardId: isMySection,
 *     resolveCardId: mapBackendWidgetKeyToCardId, // optional when API widget_key != template id
 *   };
 *
 *   constructor() {
 *     super();
 *     effect(() => {
 *       if (this.apiService.selectedProductId()) {
 *         untracked(() => this.loadPersonalizedDashboardLayout());
 *       }
 *     });
 *   }
 * }
 * ```
 *
 * ## Already using DashboardSectionLayoutService (localStorage)?
 * Nothing breaks — keep it as-is until you're ready to migrate.
 * When ready, swap in DashboardPageEngine and the backend will own the state.
 */

import { computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
// import { AuthService } from './auth.service';
import { CURRENT_USER_ACCESSOR } from './auth-user.token';
import {
  cloneLayoutItems,
  DashboardLayoutService,
  type DashboardApiWidget,
  type DashboardLoadResponse,
  type DashboardMeta,
  type WidgetCatalogEntry,
  mergeDashboardLayoutWithDefaults,
} from './dashboard-layout.service';
import {
  type DashboardCardConfig,
  type DashboardCardLayoutItem,
  type DashboardWidgetCustomConfig,
} from '../enums/dashboard-cards.enum';
import {
  DashboardWidgetPickerDialogComponent,
  type DashboardWidgetPickerAction,
  type DashboardWidgetPickerDialogResult,
} from '../../shared/components/dashboard/dashboard-widget-picker-dialog.component';
import { DashboardConfirmDialogComponent } from '../../shared/components/dashboard/dashboard-confirm-dialog.component';
import { DashboardTextPromptDialogComponent } from '../../shared/components/dashboard/dashboard-text-prompt-dialog.component';
import { DashboardWidgetCustomizeDialogComponent } from '../../shared/components/dashboard/dashboard-widget-customize-dialog.component';

// ─── Public config contract ───────────────────────────────────────────────────

export interface DashboardEngineConfig<TCardId extends string> {
  /** Backend `pages.id` for this dashboard page (used in all API calls). */
  pageId: number;
  /** Frontend card/section defaults. `id` values must match backend `widget_key`. */
  cardConfig: DashboardCardConfig<TCardId>[];
  /** Returns true when the widget_key belongs to this page's card set.
   *  Used to filter API widgets before merging with defaults. */
  isCardId: (key: string) => key is TCardId;
  /**
   * Optional mapper from API `widget_key` to template card id (e.g. backend `mr1_*` → MR1 `@switch` id).
   * When set, takes precedence over {@link isCardId} inside {@link mergeDashboardLayoutWithDefaults}.
   *
   * @param key Raw `widget_key` from GET /dashboards.
   * @returns Template card id or null when the widget is not part of this page.
   */
  resolveCardId?: (key: string) => TCardId | null;
  /**
   * Page IDs to fetch from the catalog when in mixed-dashboard mode.
   * Defaults to `[pageId]` (single-page mode) when not provided.
   */
  mixedCatalogPageIds?: number[];
  /** Optional per-action hide map for Customize Layout actions. */
  hiddenLayoutActions?: Partial<Record<DashboardWidgetPickerAction, boolean>>;
}

// ─── Engine base class ────────────────────────────────────────────────────────

export abstract class DashboardPageEngine<TCardId extends string> {
  /** Override in the concrete component to provide the page-specific config. */
  protected abstract readonly dashboardEngineConfig: DashboardEngineConfig<TCardId>;

  // ─── Services (available to subclasses via `protected`) ──────────────────
  protected readonly _layoutService = inject(DashboardLayoutService);
  protected readonly _dialog = inject(MatDialog);
  protected readonly _destroyRef = inject(DestroyRef);
  // private readonly _authService = inject(AuthService);
  // Resolved via `CURRENT_USER_ACCESSOR` (provided as `useExisting: AuthService`
  // in `app.config.ts`) so this file never statically imports `auth.service.ts`.
  // See `auth-user.token.ts` for the cycle this indirection is breaking.
  private readonly _authService = inject(CURRENT_USER_ACCESSOR);

  // ─── Layout state ─────────────────────────────────────────────────────────
  readonly dashboardId = signal<number | null>(null);
  readonly layoutItems = signal<DashboardCardLayoutItem<TCardId>[]>([]);
  readonly layoutLoading = signal(false);
  readonly layoutError = signal(false);
  readonly layoutSaving = signal(false);
  readonly isMixedDashboard = signal(false);
  readonly availableDashboards = signal<DashboardMeta[]>([]);

  /** `product_id` returned by `GET /dashboards` (any mode). */
  protected readonly cachedProductId = signal<string | number | null>(null);

  /**
   * Re-entrancy guard for `loadPersonalizedDashboardLayout()`.
   *
   * Without this, callers like the `effect(() => apiService.selectedProductId())`
   * in page components can fire two consecutive loads (once for the cached
   * session product, once for the API-hydrated one) and both end up
   * issuing the same `mixed=true&product_id=<id>` request in parallel.
   * The guard collapses concurrent invocations to one in-flight network
   * call.
   */
  private _layoutLoadInFlight = false;

  /** Cached catalog entries used by the add-widgets dialog. */
  private _mixedCatalogEntries: WidgetCatalogEntry[] = [];

  // ─── Derived state ────────────────────────────────────────────────────────

  readonly orderedDashboardCards = computed(() =>
    this.layoutItems()
      .filter((i) => i.visible && this._itemAllowedForCurrentUser(i))
      .sort((a, b) => a.order - b.order),
  );

  protected readonly allAllowedDashboardCards = computed(() =>
    this.layoutItems()
      .filter((i) => this._itemAllowedForCurrentUser(i))
      .sort((a, b) => a.order - b.order),
  );

  readonly hiddenDashboardCards = computed(() =>
    this.layoutItems()
      .filter((i) => !i.visible && this._itemAllowedForCurrentUser(i))
      .sort((a, b) => a.order - b.order),
  );

  constructor() {
    this._layoutService.saving$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((v) => this.layoutSaving.set(v));
  }

  // ─── Layout loading ───────────────────────────────────────────────────────

  /**
   * Loads the dashboard layout, transparently handling the `product_id`
   * round-trip required by the backend:
   *
   *  1. First non-mixed call → `dashboards?page_id=<n>&platform=web&mixed=false`.
   *     The `product_id` from the response is cached for future use.
   *  2. Mixed call when `product_id` is already cached →
   *     `dashboards?platform=web&mixed=true&product_id=<id>` (single request).
   *  3. Mixed call without a cached `product_id` → fires a one-time
   *     `mixed=false` request to capture it, then immediately re-issues
   *     the mixed request with `product_id` appended.
   *  4. Subsequent non-mixed calls → unchanged URL, no `product_id` sent. 
   */
  loadPersonalizedDashboardLayout(): void {
    if (this._layoutLoadInFlight) return;

    const { pageId } = this.dashboardEngineConfig;
    const isMixed = this.isMixedDashboard();

    if (isMixed && this.cachedProductId() == null) {
      this._layoutLoadInFlight = true;
      this.layoutLoading.set(true);
      this._layoutService
        .load(pageId, 'web', false)
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          if (res) {
            const pid = this._extractProductId(res);
            if (pid != null) this.cachedProductId.set(pid);
          }
          // Release the guard before chaining the real mixed request
          // so `_fetchAndApplyLayout` is allowed to proceed.
          this._layoutLoadInFlight = false;
          this._fetchAndApplyLayout(pageId, true, this.cachedProductId());
        });
      return;
    }

    this._fetchAndApplyLayout(
      pageId,
      isMixed,
      isMixed ? this.cachedProductId() : null,
    );
  }

  /**
   * Issues the actual `GET /dashboards` call and wires the response back
   * into engine state (`dashboardId`, `layoutItems`, `availableDashboards`).
   * Honors the in-flight guard so a re-entrant call is dropped instead
   * of producing a duplicate request.
   */
  private _fetchAndApplyLayout(
    pageId: number,
    isMixed: boolean,
    productId: string | number | null,
  ): void {
    if (this._layoutLoadInFlight) return;

    // Refuse mixed-mode loads that would 422 server-side (`mixed=true`
    // without a `product_id`). The bootstrap path in
    // `loadPersonalizedDashboardLayout` is supposed to populate
    // `cachedProductId` before reaching here; if it didn't (e.g. bootstrap
    // errored, returned no product id, or the consumer set
    // `isMixedDashboard=true` before any product was selected), bail out
    // quietly instead of firing a request the backend will reject.
    if (isMixed && productId == null) {
      console.warn(
        '[DashboardPageEngine] Skipping mixed=true load without product_id (would 422). pageId=',
        pageId,
      );
      this.layoutLoading.set(false);
      return;
    }

    this._layoutLoadInFlight = true;
    this.layoutLoading.set(true);

    this._layoutService
      .load(pageId, 'web', isMixed, productId)
      .pipe(
        finalize(() => {
          this.layoutLoading.set(false);
          this._layoutLoadInFlight = false;
        }),
        catchError(() => {
          this.layoutError.set(true);
          this.dashboardId.set(null);
          this.layoutItems.set(this._mergeWithDefaults([]));
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (!res) return;
        const pid = this._extractProductId(res);
        if (pid != null) this.cachedProductId.set(pid);
        this.layoutError.set(false);
        this._applyDashboardPayload(res);
        this._refreshDashboardList(res.dashboard);
      });
  }

  /**
   * Reads `product_id` from the dashboards response. Checks the top-level
   * field first (where the backend currently puts it) and falls back to
   * `dashboard.product_id` so a future schema tweak doesn't silently
   * break the cache.
   */
  private _extractProductId(
    res: DashboardLoadResponse | (DashboardLoadResponse & { product_id?: string | number }) | null,
  ): string | number | null {
    if (!res) return null;
    const top = (res as { product_id?: string | number }).product_id;
    if (top != null) return top;
    const nested = (res.dashboard as { product_id?: string | number } | null | undefined)?.product_id;
    return nested ?? null;
  }

  // ─── Template helpers ─────────────────────────────────────────────────────

  getCardTitle(card: DashboardCardLayoutItem<TCardId>): string {
    if (card.customConfig?.title?.trim()) {
      return card.customConfig.title.trim();
    }
    return (
      this.dashboardEngineConfig.cardConfig.find((c) => c.id === card.id)?.title ??
      card.id
    );
  }

  cardWidthPercent(card: DashboardCardLayoutItem<TCardId>): number {
    const span = card.colSpan ?? card.defaultCols ?? 12;
    const min = card.minCols ?? 6;
    return ((Math.min(12, Math.max(min, span))) / 12) * 100;
  }

  cardAccentStyle(card: DashboardCardLayoutItem<TCardId>): Record<string, string> {
    const hex = card.customConfig?.accentColor;
    if (!hex) return {};
    return { 'border-color': hex, 'border-width': '1px', 'border-style': 'solid' };
  }

  trackDashboardCard(_index: number, card: DashboardCardLayoutItem<TCardId>): string {
    return `${card.id}-${card.placementId ?? 'p'}-${card.widgetId}`;
  }

  // ─── Layout switcher ─────────────────────────────────────────────────────

  onDashboardLayoutSelected(event: Event): void {
    const id = Number((event.target as HTMLSelectElement).value);
    this._selectDashboardLayoutById(id);
  }

  // ─── Layout manager dialog (entry point from toolbar) ────────────────────

  openLayoutManagerDialog(): void {
    const cards = this.layoutItems()
      .filter((card) => this._itemAllowedForCurrentUser(card))
      .sort((a, b) => a.order - b.order)
      .map((card) => ({
        key: this._cardSelectionKey(card),
        title: this.getCardTitle(card),
        selected: card.visible,
        sourcePageName: card.sourcePageName ?? null,
        widgetId: card.widgetId,
        sourcePageId: card.sourcePageId ?? this.dashboardEngineConfig.pageId,
        colSpan: card.colSpan ?? null,
        rowSpan: card.rowSpan ?? null,
        customConfig: card.customConfig ?? null,
      }));

    this._dialog
      .open(DashboardWidgetPickerDialogComponent, {
        maxWidth: '780px',
        data: {
          mode: 'layout',
          pageId: this.dashboardEngineConfig.pageId,
          entries: [],
          selectedIds: [],
          cards,
          currentLayoutId: this.dashboardId(),
          isMixedMode: this.isMixedDashboard(),
          layoutError: this.layoutError(),
          layouts: this.availableDashboards(),
          hiddenLayoutActions: this.dashboardEngineConfig.hiddenLayoutActions,
        },
      })
      .afterClosed()
      .subscribe((result: DashboardWidgetPickerDialogResult | undefined) => {
        if (!result) return;

        if (result.kind === 'layoutVisibility') {
          if (result.mixedContext?.isMixed) {
            this._applyMixedDashboardVisibility(result.mixedContext);
            return;
          }
          if (this.isMixedDashboard()) {
            this.isMixedDashboard.set(false);
          }
          this._applyDashboardVisibilitySelection(result.selectedKeys, {
            resetLayoutToDefaults: result.resetLayoutToDefaults === true,
          });
          return;
        }

        if (result.kind !== 'layoutAction') return;

        switch (result.action) {
          case 'saveAs':      this.saveLayoutAsNew();            break;
          case 'setDefault':  this.setCurrentLayoutDefault();    break;
          case 'delete':      this.deleteCurrentLayout();        break;
          case 'toggleMixed': this.toggleMixedMode();            break;
          case 'widgets':     this.openAddWidgetsDialog();       break;
          case 'reset':       this.confirmResetDashboard();      break;
          case 'selectLayout':
            if (result.dashboardId) {
              this._selectDashboardLayoutById(result.dashboardId);
            }
            break;
        }
      });
  }

  // ─── Layout save-as ───────────────────────────────────────────────────────

  saveLayoutAsNew(): void {
    this._dialog
      .open(DashboardTextPromptDialogComponent, {
        width: '400px',
        data: { title: 'Save layout', label: 'Layout name', initialValue: 'My layout' },
      })
      .afterClosed()
      .subscribe((name: string | undefined) => {
        if (!name?.trim()) return;
        const { pageId } = this.dashboardEngineConfig;
        this._prepareWidgetPayload(this.layoutItems()).subscribe((widget_ids) => {
          if (!widget_ids.length) return;
          this._layoutService
            .createDashboard({
              name: name.trim(),
              type: this.isMixedDashboard() ? 'mixed' : 'product_page',
              platform: 'web',
              page_id: this.isMixedDashboard() ? null : pageId,
              widget_ids,
            })
            .pipe(catchError(() => of(null)))
            .subscribe((res) => {
              if (res) {
                this._applyDashboardPayload(res);
                this._refreshDashboardList(res.dashboard);
              }
            });
        });
      });
  }

  // ─── Layout set-default ───────────────────────────────────────────────────

  setCurrentLayoutDefault(): void {
    const id = this.dashboardId();
    if (!id) return;
    this._layoutService
      .setDefaultDashboard(id)
      .pipe(catchError(() => of(null)))
      .subscribe(() => this._refreshDashboardListMetaOnly());
  }

  // ─── Layout delete ────────────────────────────────────────────────────────

  deleteCurrentLayout(): void {
    const id = this.dashboardId();
    if (!id) return;
    this._dialog
      .open(DashboardConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'Delete layout',
          message: 'Delete this saved dashboard layout? This cannot be undone.',
          confirmLabel: 'Delete',
        },
      })
      .afterClosed()
      .subscribe((ok: boolean | undefined) => {
        if (!ok) return;
        this._layoutService
          .deleteDashboard(id)
          .pipe(catchError(() => of(null)))
          .subscribe(() => {
            this.isMixedDashboard.set(false);
            this.loadPersonalizedDashboardLayout();
          });
      });
  }

  // ─── Mixed-mode toggle ────────────────────────────────────────────────────

  toggleMixedMode(): void {
    this.isMixedDashboard.update((v) => !v);
    this.loadPersonalizedDashboardLayout();
  }

  // ─── Add widgets dialog ───────────────────────────────────────────────────

  openAddWidgetsDialog(): void {
    const { pageId, isCardId } = this.dashboardEngineConfig;
    const catalogPageIds = this.isMixedDashboard()
      ? this._effectiveMixedCatalogPageIds()
      : [pageId];

    const requests = catalogPageIds.map((pId) =>
      this._layoutService
        .getWidgetCatalog(pId, 'web')
        .pipe(catchError(() => of([] as WidgetCatalogEntry[]))),
    );

    forkJoin(requests).subscribe((chunks) => {
      this._mixedCatalogEntries = chunks
        .flat()
        .filter((e) => this._catalogEntryAllowed(e));

      const currentIds = this.layoutItems()
        .map((i) => i.widgetId)
        .filter((wid) => wid > 0);

      // Pre-filter entries to this page's widgets when not in mixed mode,
      // making the picker generic without modifying the dialog component.
      const entriesToShow = this.isMixedDashboard()
        ? this._mixedCatalogEntries
        : this._mixedCatalogEntries.filter((e) => isCardId(e.widget_key));

      this._dialog
        .open(DashboardWidgetPickerDialogComponent, {
          maxWidth: '780px',
          data: {
            mode: 'widgets',
            pageId,
            entries: entriesToShow,
            selectedIds: currentIds,
          },
        })
        .afterClosed()
        .subscribe((result: DashboardWidgetPickerDialogResult | undefined) => {
          if (!result || result.kind !== 'widgets') return;
          const selected = result.selectedIds;
          if (!selected?.length) return;

          const byId = new Map(this._mixedCatalogEntries.map((e) => [e.id, e]));
          const currentItems = new Map(this.layoutItems().map((i) => [i.widgetId, i]));

          const widget_ids = selected
            .map((id) => {
              const e = byId.get(id);
              if (!e) return null;
              const existing = currentItems.get(e.id);
              return {
                widget_id: e.id,
                source_page_id: e.page_id ?? undefined,
                col_span: existing?.colSpan ?? e.default_cols ?? null,
                row_span: existing?.rowSpan ?? null,
                custom_config: existing?.customConfig ?? null,
              };
            })
            .filter(Boolean) as Array<{
              widget_id: number;
              source_page_id?: number | null;
              col_span?: number | null;
              row_span?: number | null;
              custom_config?: Record<string, any> | null;
            }>;

          if (this.isMixedDashboard()) {
            this._layoutService
              .createDashboard({
                name: 'Mixed dashboard',
                type: 'mixed',
                platform: 'web',
                page_id: null,
                widget_ids,
              })
              .pipe(catchError(() => of(null)))
              .subscribe((res) => {
                if (res) this._applyDashboardPayload(res);
              });
          } else {
            const dashId = this.dashboardId();
            if (dashId) {
              this._layoutService.save(dashId, widget_ids.map((w) => w.widget_id));
            }
          }
        });
    });
  }

  // ─── Drag-drop reorder ────────────────────────────────────────────────────

  onDashboardCardDrop(
    event: CdkDragDrop<DashboardCardLayoutItem<TCardId>[]>,
  ): void {
    if (event.previousIndex === event.currentIndex) return;

    const visible = cloneLayoutItems(this.orderedDashboardCards());
    moveItemInArray(visible, event.previousIndex, event.currentIndex);

    const hidden = cloneLayoutItems(this.layoutItems())
      .sort((a, b) => a.order - b.order)
      .filter((i) => !i.visible);

    const merged = [...visible, ...hidden];
    merged.forEach((row, idx) => { row.order = idx; });

    this.layoutItems.set(merged);

    const dashId = this.dashboardId();
    if (!dashId) return;

    const orderedVisibleIds = visible
      .filter((i) => i.widgetId > 0)
      .map((i) => i.widgetId);

    if (!orderedVisibleIds.length) return;

    this._layoutService.save(dashId, orderedVisibleIds);
  }

  // ─── Visibility toggle ────────────────────────────────────────────────────

  toggleDashboardCardVisibility(card: DashboardCardLayoutItem<TCardId>): void {
    const dashId = this.dashboardId();
    const catalogWidgetId = card.widgetId;
    if (dashId && catalogWidgetId > 0) {
      this._layoutService
        .toggleVisibility(dashId, catalogWidgetId, { is_visible: !card.visible })
        .pipe(catchError(() => of(null)))
        .subscribe(() => this.loadPersonalizedDashboardLayout());
    } else {
      this.layoutItems.update((items) =>
        items.map((i) => (i.id === card.id ? { ...i, visible: !i.visible } : i)),
      );
    }
  }

  // ─── Column width cycling ─────────────────────────────────────────────────

  cycleCardWidth(card: DashboardCardLayoutItem<TCardId>): void {
    const cur = card.colSpan ?? card.defaultCols ?? 12;
    const seq = [6, 8, 12];
    const next = seq[(seq.indexOf(cur) + 1) % seq.length];
    const clamped = Math.min(12, Math.max(card.minCols ?? 6, next));

    this.layoutItems.update((items) =>
      items.map((i) =>
        i.placementId === card.placementId && i.id === card.id
          ? { ...i, colSpan: clamped }
          : i,
      ),
    );

    const dashId = this.dashboardId();
    if (dashId && card.placementId) {
      this._layoutService
        .updateWidgetSize(dashId, card.placementId, clamped, card.rowSpan ?? null)
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
  }

  // ─── Per-widget customization dialog ─────────────────────────────────────

  customizeDashboardCard(card: DashboardCardLayoutItem<TCardId>): void {
    this._dialog
      .open(DashboardWidgetCustomizeDialogComponent, {
        width: '400px',
        data: {
          title: `Customize: ${this.getCardTitle(card)}`,
          initial: card.customConfig ?? null,
        },
      })
      .afterClosed()
      .subscribe((cfg: DashboardWidgetCustomConfig | null | undefined) => {
        if (cfg === undefined) return;
        const dashId = this.dashboardId();
        if (dashId && card.placementId) {
          this._layoutService
            .patchWidgetCustomConfig(dashId, card.placementId, cfg)
            .pipe(catchError(() => of(null)))
            .subscribe(() => {
              this.layoutItems.update((items) =>
                items.map((i) =>
                  i.placementId === card.placementId
                    ? { ...i, customConfig: cfg ?? undefined }
                    : i,
                ),
              );
            });
        } else {
          this.layoutItems.update((items) =>
            items.map((i) =>
              i.id === card.id ? { ...i, customConfig: cfg ?? undefined } : i,
            ),
          );
        }
      });
  }

  // ─── Reset dashboard dialog ───────────────────────────────────────────────

  confirmResetDashboard(): void {
    const id = this.dashboardId();
    if (!id) return;
    this._dialog
      .open(DashboardConfirmDialogComponent, {
        width: '440px',
        data: {
          title: 'Reset dashboard',
          message:
            'Restore the default widget layout for this page? Your current ordering and visibility changes will be reset.',
          confirmLabel: 'Reset to default',
        },
      })
      .afterClosed()
      .subscribe((ok: boolean | undefined) => {
        if (!ok) return;
        this._layoutService
          .reset(id)
          .pipe(catchError(() => of(null)))
          .subscribe(() => this.loadPersonalizedDashboardLayout());
      });
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private _userRoleId(): number | null {
    const u = this._authService.currentUser() as Record<string, unknown> | null;
    if (!u) return null;
    const rid = u['role_id'] ?? u['roleId'];
    return typeof rid === 'number' ? rid : null;
  }

  private _itemAllowedForCurrentUser(item: DashboardCardLayoutItem<TCardId>): boolean {
    const ids = item.allowedRoleIds;
    if (!ids?.length) return true;
    const rid = this._userRoleId();
    if (rid == null) return false;
    return ids.includes(rid);
  }

  private _catalogEntryAllowed(e: WidgetCatalogEntry): boolean {
    const perms = e.widget_role_permissions;
    if (!perms?.length) return true;
    const rid = this._userRoleId();
    if (rid == null) return false;
    return perms.some((p) => p.role_id === rid && p.can_view !== false);
  }

  private _cardSelectionKey(card: DashboardCardLayoutItem<TCardId>): string {
    return `${card.id}::${card.placementId ?? card.widgetId}`;
  }

  private _effectiveMixedCatalogPageIds(): number[] {
    const { pageId, mixedCatalogPageIds } = this.dashboardEngineConfig;
    return mixedCatalogPageIds ?? [pageId];
  }

  private _mergeWithDefaults(
    apiWidgets: DashboardApiWidget[],
  ): DashboardCardLayoutItem<TCardId>[] {
    const { cardConfig, isCardId, resolveCardId } = this.dashboardEngineConfig;
    return mergeDashboardLayoutWithDefaults(apiWidgets, cardConfig, {
      userRoleId: this._userRoleId(),
      isCardId,
      resolveCardId,
    });
  }

  /**
   * Applies GET /dashboards payload: normalizes dashboard id and merges widgets (including snapshot-hydrated rows from backend).
   *
   * @param res Dashboard + widgets from `DashboardLayoutService.load` / `loadByDashboardId`.
   */
  private _applyDashboardPayload(res: DashboardLoadResponse): void {
    const rawId = res.dashboard.id;
    const dashId = typeof rawId === 'string' ? Number(rawId) : rawId;
    this.dashboardId.set(
      typeof dashId === 'number' && Number.isFinite(dashId) && dashId > 0 ? dashId : null,
    );
    this.layoutItems.set(this._mergeWithDefaults(res.widgets));
  }

  private _refreshDashboardList(fallback: DashboardMeta): void {
    const { pageId } = this.dashboardEngineConfig;
    const isMixed = this.isMixedDashboard();
    const productId = this.cachedProductId();

    // ── Refuse mixed-mode list refresh without product_id.
    //
    // Without this, every successful per-page `load()` triggered a
    // sibling `listDashboards(null, 'web', true)` call — i.e.
    // `dashboards?platform=web&mixed=true` with no `product_id` — and
    // the backend rejected each one with a 422. With N embedded MR1/MR2
    // instances mounted on the My Dashboard page, that produced N 422s
    // per layout-load round-trip (one per child instance). We bail out
    // and seed `availableDashboards` from the fallback so any UI bound
    // to this signal (e.g. the layout-switcher dropdown) still has a
    // sensible value.
    if (isMixed && productId == null) {
      this.availableDashboards.set([fallback]);
      return;
    }

    this._layoutService
      .listDashboards(
        isMixed ? null : pageId,
        'web',
        isMixed,
        isMixed ? productId : null,
      )
      .subscribe((list) => {
        this.availableDashboards.set(list?.length ? list : [fallback]);
      });
  }

  private _refreshDashboardListMetaOnly(): void {
    const { pageId } = this.dashboardEngineConfig;
    this._layoutService
      .listDashboards(pageId, 'web')
      .subscribe((list) => {
        if (list?.length) this.availableDashboards.set(list);
      });
  }

  private _selectDashboardLayoutById(id: number): void {
    if (!id || id === this.dashboardId()) return;
    this._layoutService
      .loadByDashboardId(id, 'web')
      .pipe(
        catchError(() => {
          this.loadPersonalizedDashboardLayout();
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) this._applyDashboardPayload(res);
      });
  }

  /**
   * Persists Customize Layout visibility (and optionally restores default layout from snapshot).
   *
   * @param selectedKeys Dialog selection keys built from {@link _cardSelectionKey}.
   * @param options When `resetLayoutToDefaults` is true and a dashboard exists, calls POST reset first.
   * @returns Void; may trigger async HTTP and reload.
   */
  private _applyDashboardVisibilitySelection(
    selectedKeys: string[],
    options?: { resetLayoutToDefaults?: boolean },
  ): void {
    const resetLayoutToDefaults = options?.resetLayoutToDefaults === true;
    const dashId = this.dashboardId();

    if (resetLayoutToDefaults && dashId) {
      this._layoutService
        .reset(dashId)
        .pipe(catchError(() => of(null)))
        .subscribe((res) => {
          if (res != null) {
            this.loadPersonalizedDashboardLayout();
            return;
          }
          this._applyDashboardVisibilitySelection(selectedKeys, {
            resetLayoutToDefaults: false,
          });
        });
      return;
    }

    const selected = new Set(selectedKeys);
    let visibleItems = this.layoutItems().filter((card) =>
      selected.has(this._cardSelectionKey(card)),
    );
    if (!visibleItems.length) return;

    if (resetLayoutToDefaults) {
      visibleItems = this._sortLayoutItemsByCardDefaultOrder(visibleItems).map((i) => ({
        ...i,
        colSpan: null,
        rowSpan: null,
        customConfig: null,
      }));
    }

    this._prepareWidgetPayload(visibleItems).subscribe((widget_ids) => {
      if (!widget_ids.length) return;
      const { pageId } = this.dashboardEngineConfig;
      const currentDash = this.availableDashboards().find(
        (d) => d.id === this.dashboardId(),
      );
      const name = currentDash?.name || 'My Dashboard';
      const dashIdForBatch = this.dashboardId();

      if (dashIdForBatch) {
        const batchWidgets = widget_ids.map(w => ({
          widget_id: String(w.widget_id),
          source_page_id: String(w.source_page_id || pageId),
          col_span: w.col_span ?? 12,
          row_span: w.row_span ?? 1,
          custom_config: w.custom_config || {}
        }));

        this._layoutService
          .batchUpdateWidgets(dashIdForBatch, { widgets: batchWidgets })
          .pipe(catchError(() => of(null)))
          .subscribe(() => {
            this.loadPersonalizedDashboardLayout();
          });
      } else {
        const legacyWidgetIds = widget_ids.map(w => ({
          widget_id: Number(w.widget_id),
          source_page_id: w.source_page_id ? Number(w.source_page_id) : pageId,
          col_span: w.col_span,
          row_span: w.row_span,
          custom_config: w.custom_config
        }));

        this._layoutService
          .createDashboard({
            name,
            type: this.isMixedDashboard() ? 'mixed' : 'product_page',
            platform: 'web',
            page_id: this.isMixedDashboard() ? null : pageId,
            widget_ids: legacyWidgetIds,
          })
          .pipe(catchError(() => of(null)))
          .subscribe(() => {
            this.loadPersonalizedDashboardLayout();
          });
      }
    });
  }

  /**
   * Orders layout rows by each card's configured {@link DashboardCardConfig.defaultOrder}.
   *
   * @param items Layout rows belonging to the current page selection.
   * @returns A new array sorted ascending by default order (unknown ids sort last).
   */
  private _sortLayoutItemsByCardDefaultOrder(
    items: DashboardCardLayoutItem<TCardId>[],
  ): DashboardCardLayoutItem<TCardId>[] {
    const { cardConfig } = this.dashboardEngineConfig;
    const orderOf = (id: TCardId): number => {
      const cfg = cardConfig.find((c) => c.id === id);
      return cfg?.defaultOrder ?? 9999;
    };
    return [...items].sort((a, b) => orderOf(a.id) - orderOf(b.id));
  }

  /**
   * Applies mixed dashboard card visibility selection to the mixed layout only.
   *
   * @param ctx Mixed dialog payload with target dashboard id and visible widget set.
   * @returns Void; triggers mixed dashboard reload on success.
   */
  private _applyMixedDashboardVisibility(ctx: {
    isMixed: boolean;
    dashboardId: number | null;
    visibleWidgets: Array<{
      widget_id: string;
      source_page_id?: string | null;
      col_span?: number | null;
      row_span?: number | null;
      custom_config?: Record<string, any> | null;
    }>;
  }): void {
    const visibleWidgets = ctx.visibleWidgets.map((row) => ({
      widget_id: String(row.widget_id),
      source_page_id: row.source_page_id != null ? String(row.source_page_id) : undefined,
      col_span: row.col_span ?? null,
      row_span: row.row_span ?? null,
      custom_config: row.custom_config ?? null,
    }));
    if (ctx.dashboardId) {
      if (!visibleWidgets.length) return;
      this._layoutService
        .batchUpdateWidgets(ctx.dashboardId, { widgets: visibleWidgets })
        .pipe(catchError(() => of(null)))
        .subscribe(() => {
          // Mixed dashboard should be updated in backend, but current page view
          // must remain on product_page layout.
          this.isMixedDashboard.set(false);
          this.loadPersonalizedDashboardLayout();
        });
      return;
    }
    if (!visibleWidgets.length) {
      return;
    }
    this._layoutService
      .createDashboard({
        name: 'My Dashboard',
        type: 'mixed',
        platform: 'web',
        page_id: null,
        widget_ids: visibleWidgets.map((row) => ({
          widget_id: Number(row.widget_id),
          source_page_id: row.source_page_id != null ? Number(row.source_page_id) : null,
          col_span: row.col_span ?? null,
          row_span: row.row_span ?? null,
          custom_config: row.custom_config ?? null,
        })),
      })
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (!res) return;
        // After creating/updating mixed dashboard, keep user on current page layout.
        this.isMixedDashboard.set(false);
        this.loadPersonalizedDashboardLayout();
      });
  }

  private _prepareWidgetPayload(
    items: DashboardCardLayoutItem<TCardId>[],
  ): Observable<any[]> {
    const { pageId, resolveCardId } = this.dashboardEngineConfig;
    const basePayload = (i: DashboardCardLayoutItem<TCardId>) => ({
      widget_id: i.widgetId,
      source_page_id: i.sourcePageId ?? pageId,
      col_span: i.colSpan,
      row_span: i.rowSpan,
      custom_config: i.customConfig,
    });

    if (items.every((i) => i.widgetId > 0)) {
      return of(items.map(basePayload));
    }

    const catalogPageIds = this._effectiveMixedCatalogPageIds();
    const requests = catalogPageIds.map((id) =>
      this._layoutService
        .getWidgetCatalog(id, 'web')
        .pipe(catchError(() => of([]))),
    );

    return forkJoin(requests).pipe(
      map((chunks) => {
        const catalog: WidgetCatalogEntry[] = (chunks as WidgetCatalogEntry[][]).flat();
        const keyToId = new Map<string, number>();
        const cardIdToWidgetId = new Map<string, number>();
        catalog.forEach((e) => {
          if (e.widget_key) keyToId.set(e.widget_key.toLowerCase(), e.id);
          if (!resolveCardId || !e.widget_key) return;
          const cardId = resolveCardId(e.widget_key);
          if (cardId) {
            cardIdToWidgetId.set(cardId.toLowerCase(), e.id);
          }
        });

        return items
          .map((i) => {
            let wid = i.widgetId;
            if (wid <= 0) {
              const idKey = i.id.toLowerCase();
              wid = keyToId.get(idKey) ?? cardIdToWidgetId.get(idKey) ?? 0;
            }
            if (wid <= 0) return null;
            return basePayload({ ...i, widgetId: wid });
          })
          .filter(Boolean) as any[];
      }),
    );
  }
}
